import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AppDispatch, RootState, useAppDispatch } from '../../../redux/store'
import {
  fetchActionPriorityCriticalRatio,
  fetchActionPriorityHighRatio,
  fetchActionPriorityLowRatio,
  fetchActionPriorityMediumRatio,
} from '../../../redux/missions/missionActions'
import { getRisques } from '../../../redux/risques/risqueActions'
import { getActionsByMissionId } from '../../../redux/actions/actionActions'
import { Mission } from '../../../types/missionTypes'
import { Action } from '../../../types/actionTypes'
import { Risque } from '../../../types/risqueTypes'
import './DashboardMissionsTab.css'

const COLORS = {
  coral: '#D14A3E',
  coralSoft: '#FAE6E3',
  amber: '#C98A16',
  amberSoft: '#FAEFDA',
  brand: '#00146D',
  brandSoft: '#E6EAF6',
  teal: '#1FBFA3',
  tealSoft: '#E2F7F2',
  muted2: '#8B99B4',
  lineSoft: '#EAEFF7',
  line: '#DEE4F0',
}

const PRIO_ICONS: Record<string, string> = {
  critical: 'M8 2.5l6 11H2z',
  high: 'M8 12V4M4.5 7.5L8 4l3.5 3.5',
  medium: 'M3 8h10M9.5 4.5L13 8l-3.5 3.5',
  low: 'M8 4v8M4.5 8.5L8 12l3.5-3.5',
}

function Donut({
  segs,
}: {
  segs: { k: string; v: number; c: string }[]
}) {
  const total = segs.reduce((a, s) => a + s.v, 0) || 1
  const C = 2 * Math.PI * 44
  let off = 0
  const rings = segs.map((s) => {
    const len = (C * s.v) / total
    const dash = `${len.toFixed(2)} ${(C - len).toFixed(2)}`
    const el = (
      <circle
        key={s.k}
        cx="60"
        cy="60"
        r="44"
        fill="none"
        stroke={s.c}
        strokeWidth="17"
        strokeDasharray={dash}
        strokeDashoffset={(-off).toFixed(2)}
        transform="rotate(-90 60 60)"
      />
    )
    off += len
    return el
  })
  return (
    <svg viewBox="0 0 120 120" className="dmt-donut" aria-hidden>
      {rings}
    </svg>
  )
}

function statusKey(status?: string) {
  return (status || '').trim().toLowerCase()
}

function isInProgress(a: Action) {
  return statusKey(a.Status) === 'en cours'
}
function isDone(a: Action) {
  return statusKey(a.Status) === 'terminé' || statusKey(a.Status) === 'termine'
}
function isTodo(a: Action) {
  const s = statusKey(a.Status)
  return s === 'a faire' || s === 'à faire' || s === 'en attente'
}

type Props = {
  missions: Mission[]
  selectedMissionId?: string
  onSelectMission: (id: string) => void
}

export default function DashboardMissionsTab({
  missions,
  selectedMissionId,
  onSelectMission,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch: AppDispatch = useAppDispatch()

  const mission = missions.find((m) => m._id === selectedMissionId) || missions[0]

  const criticalRatio = useSelector(
    (state: RootState) => state.mission.critical?.ratio || 0
  )
  const highRatio = useSelector(
    (state: RootState) => state.mission.high?.ratio || 0
  )
  const mediumRatio = useSelector(
    (state: RootState) => state.mission.medium?.ratio || 0
  )
  const lowRatio = useSelector(
    (state: RootState) => state.mission.low?.ratio || 0
  )

  const risques = useSelector(
    (state: RootState) => state.risque.RisqueResponse?.risques ?? state.risque.risques ?? []
  ) as Risque[]
  const openRiskTotal = useSelector(
    (state: RootState) => state.risque.RisqueResponse?.total ?? 0
  )

  const actions = useSelector(
    (state: RootState) => state.action.ActionResponse?.actions ?? []
  ) as Action[]

  useEffect(() => {
    const id = mission?._id
    if (!id) return
    dispatch(fetchActionPriorityCriticalRatio(id))
    dispatch(fetchActionPriorityHighRatio(id))
    dispatch(fetchActionPriorityMediumRatio(id))
    dispatch(fetchActionPriorityLowRatio(id))
    dispatch(getActionsByMissionId({ MissionId: id, page: 1, pageSize: 200 }))
  }, [mission?._id, dispatch])

  useEffect(() => {
    dispatch(getRisques({ search: '', page: 1, pageSize: 500 }))
  }, [dispatch])

  const prioCards = useMemo(() => {
    const rows = [
      {
        key: 'critical',
        label: t('homePage.missionPriority.critical'),
        ratio: criticalRatio,
        c: COLORS.coral,
        soft: COLORS.coralSoft,
      },
      {
        key: 'high',
        label: t('homePage.missionPriority.high'),
        ratio: highRatio,
        c: COLORS.amber,
        soft: COLORS.lineSoft,
      },
      {
        key: 'medium',
        label: t('homePage.missionPriority.medium'),
        ratio: mediumRatio,
        c: COLORS.brand,
        soft: COLORS.lineSoft,
      },
      {
        key: 'low',
        label: t('homePage.missionPriority.low'),
        ratio: lowRatio,
        c: COLORS.teal,
        soft: COLORS.lineSoft,
      },
    ]
    return rows.map((r) => {
      const pct = Math.round((r.ratio || 0) * 1000) / 10
      const active = pct > 0
      return {
        ...r,
        pct,
        active,
        sub: active
          ? t('homePage.missionPriority.shareOfMissionTasks')
          : t('homePage.missionPriority.noTask', { defaultValue: 'no task' }),
      }
    })
  }, [criticalRatio, highRatio, mediumRatio, lowRatio, t])

  const riskStats = useMemo(() => {
    const buckets = { 1: 0, 2: 0, 3: 0, 4: 0 }
    let unassigned = 0
    risques.forEach((r) => {
      const c = Number(r.Criticality) || 0
      if (c >= 1 && c <= 4) buckets[c as 1 | 2 | 3 | 4] += 1
      if (!r.PlanId && !r.PlanName) unassigned += 1
    })
    const segs = [
      { k: t('homePage.missionPriority.critical'), v: buckets[1], c: '#D14A3E' },
      { k: t('homePage.missionPriority.high'), v: buckets[2], c: '#C98A16' },
      { k: t('homePage.missionPriority.medium'), v: buckets[3], c: '#3B76D8' },
      { k: t('homePage.missionPriority.low'), v: buckets[4], c: '#1FBFA3' },
    ]
    const total = segs.reduce((a, s) => a + s.v, 0)
    return { segs, total, unassigned }
  }, [risques, t])

  const taskStats = useMemo(() => {
    const now = new Date()
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)

    let inProgress = 0
    let done = 0
    let todo = 0
    let overdue = 0
    let dueThisMonth = 0
    let noProject = 0

    actions.forEach((a) => {
      if (isInProgress(a)) inProgress += 1
      else if (isDone(a)) done += 1
      else if (isTodo(a)) todo += 1

      const end = a.DateFin ? new Date(a.DateFin) : null
      if (end && !isDone(a) && end < now) overdue += 1
      if (end && end >= monthStart && end <= monthEnd) dueThisMonth += 1
      if (!a.ProjetId && !a.ProjetName) noProject += 1
    })

    const total = Math.max(actions.length, 1)
    const stack = [
      { key: 'progress', n: inProgress, c: COLORS.brand, w: (inProgress / total) * 100 },
      { key: 'done', n: done, c: COLORS.teal, w: (done / total) * 100 },
      { key: 'todo', n: todo, c: COLORS.line, w: (todo / total) * 100 },
    ]
    const miniMax = Math.max(overdue, dueThisMonth, noProject, 1)
    const mini = [
      {
        k: t('homePage.missionsTab.overdue', { defaultValue: 'Overdue' }),
        v: overdue,
        c: COLORS.coral,
      },
      {
        k: t('homePage.missionsTab.dueThisMonth', {
          defaultValue: 'Due this month',
        }),
        v: dueThisMonth,
        c: COLORS.amber,
      },
      {
        k: t('homePage.missionsTab.noProject', {
          defaultValue: 'No project attached',
        }),
        v: noProject,
        c: COLORS.muted2,
      },
    ].map((r) => ({ ...r, w: (r.v / miniMax) * 100 }))

    return { inProgress, done, todo, stack, mini }
  }, [actions, t])

  const missionCount = missions.length
  const [menuOpen, setMenuOpen] = useState(false)
  const selectRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!menuOpen) return
    const onDoc = (e: MouseEvent) => {
      if (!selectRef.current?.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  const selectedLabel =
    mission?.MissionName ||
    t('homePage.missionPriority.noMission')

  return (
    <div className="dmt">
      <div className="dmt-sec-title">
        <h3>{t('homePage.missionPriority.taskPriorities')}</h3>
        <div
          className={`dmt-mission-select${menuOpen ? ' is-open' : ''}`}
          ref={selectRef}
        >
          <button
            type="button"
            className="dmt-mission-trigger"
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            aria-label={t('homePage.missionPriority.taskPriorities')}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="dmt-mission-trigger-ic" aria-hidden>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9.5 2.5c2.5 0 4 1.5 4 4L9 11l-2.5.5L5 9.5 9.5 5" />
                <path d="M5.5 10.5L3 13" />
                <circle cx="10.5" cy="5.5" r="1" />
              </svg>
            </span>
            <span className="dmt-mission-trigger-text">
              <span className="dmt-mission-trigger-eyebrow">
                {t('missions', { defaultValue: 'Mission' })}
              </span>
              <span className="dmt-mission-trigger-label">{selectedLabel}</span>
            </span>
            <span className="dmt-mission-chevron" aria-hidden>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M4 6.5L8 10.5L12 6.5" />
              </svg>
            </span>
          </button>

          {menuOpen && (
            <div className="dmt-mission-menu" role="listbox">
              <div className="dmt-mission-menu-scroll">
                {missions.length === 0 ? (
                  <div className="dmt-mission-empty">
                    {t('homePage.missionPriority.noMission')}
                  </div>
                ) : (
                  missions.map((m) => {
                    const selected = m._id === mission?._id
                    return (
                      <button
                        key={m._id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`dmt-mission-option${selected ? ' is-selected' : ''}`}
                        onClick={() => {
                          if (m._id) onSelectMission(m._id)
                          setMenuOpen(false)
                        }}
                      >
                        <span className="dmt-mission-option-dot" aria-hidden />
                        <span className="dmt-mission-option-label">{m.MissionName}</span>
                        {selected && (
                          <svg
                            className="dmt-mission-check"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.7"
                            aria-hidden
                          >
                            <path d="M3.5 8.2l3 3 6-6.5" />
                          </svg>
                        )}
                      </button>
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="dmt-prio-grid">
        {prioCards.map((p) => (
          <button
            key={p.key}
            type="button"
            className="dmt-prio-card"
            onClick={() => {
              if (mission?._id) {
                navigate(`/mission-priority/${p.label}/${mission._id}`)
              }
            }}
          >
            <div className="dmt-prio-top">
              <span
                className="dmt-prio-ic"
                style={{
                  color: p.active ? p.c : COLORS.muted2,
                  background: p.active ? p.soft : COLORS.lineSoft,
                }}
              >
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                  <path d={PRIO_ICONS[p.key]} />
                </svg>
              </span>
              <span className="dmt-eyebrow">{p.label}</span>
              <span
                className="dmt-prio-v"
                style={{ color: p.active ? p.c : COLORS.muted2 }}
              >
                {p.pct}%
              </span>
            </div>
            <div className="dmt-bar-track">
              <span style={{ width: `${Math.min(p.pct, 100)}%`, background: p.c }} />
            </div>
            <div className="dmt-prio-sub">
              <span>{t('homePage.missionPriority.shareOfMissionTasks')}</span>
              <b>{p.active ? `${p.pct}%` : p.sub}</b>
            </div>
          </button>
        ))}
      </div>

      <div className="dmt-bottom-grid">
        <div className="dmt-card dmt-flexcard">
          <div className="dmt-card-head">
            <h3>{t('homePage.missionsTab.openRisks', { defaultValue: 'Open risks' })}</h3>
            <button
              type="button"
              className="dmt-hint"
              onClick={() => navigate('/risques')}
            >
              {t('homePage.missionsTab.openRegister', {
                defaultValue: 'Open register →',
              })}
            </button>
          </div>
          <div className="dmt-card-body dmt-flexbody">
            <div className="dmt-donut-wrap">
              <Donut segs={riskStats.segs} />
              <div className="dmt-donut-note">
                <b>{openRiskTotal || riskStats.total}</b>
                {t('homePage.missionsTab.openAcross', {
                  count: missionCount,
                  defaultValue:
                    missionCount === 1
                      ? 'open for this programme'
                      : `open across ${missionCount} missions`,
                })}
              </div>
            </div>
            <div className="dmt-legend-row">
              {riskStats.segs.map((s) => (
                <span key={s.k}>
                  <i style={{ background: s.c }} />
                  {s.k} <b className="dmt-mono">{s.v}</b>
                </span>
              ))}
            </div>
            {riskStats.unassigned > 0 && (
              <div className="dmt-risk-note">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="8" cy="8" r="6.2" />
                  <path d="M8 7.2V11M8 5.2h.01" />
                </svg>
                <span>
                  <b>{riskStats.unassigned}</b>{' '}
                  {t('homePage.missionsTab.noOwner', {
                    defaultValue: 'of them have no treatment plan yet.',
                  })}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="dmt-card dmt-flexcard">
          <div className="dmt-card-head">
            <h3>
              {t('homePage.missionsTab.tasksProgress', {
                defaultValue: 'Tasks progress',
              })}
            </h3>
            <button
              type="button"
              className="dmt-hint"
              onClick={() => navigate('/actions')}
            >
              {t('homePage.missionsTab.openTasks', {
                defaultValue: 'Open tasks →',
              })}
            </button>
          </div>
          <div className="dmt-card-body dmt-flexbody">
            <div className="dmt-tp-top">
              <span className="dmt-tp-n">{taskStats.inProgress}</span>
              <span className="dmt-tp-l">
                {taskStats.done === 0
                  ? t('homePage.missionsTab.inProgressLede', {
                      defaultValue: 'tasks in progress, none closed yet',
                    })
                  : t('homePage.missionsTab.inProgressLedeSome', {
                      defaultValue: 'tasks in progress',
                    })}
              </span>
            </div>
            <div className="dmt-stack-bar">
              {taskStats.stack
                .filter((s) => s.n > 0)
                .map((s) => (
                  <span
                    key={s.key}
                    style={{ width: `${s.w}%`, background: s.c }}
                  />
                ))}
            </div>
            <div className="dmt-legend-row dmt-legend-row--start">
              <span>
                <i style={{ background: COLORS.brand }} />
                {t('homePage.missionsTab.inProgress', {
                  defaultValue: 'In progress',
                })}{' '}
                <b className="dmt-mono">{taskStats.inProgress}</b>
              </span>
              <span>
                <i style={{ background: COLORS.teal }} />
                {t('homePage.missionsTab.done', { defaultValue: 'Done' })}{' '}
                <b className="dmt-mono">{taskStats.done}</b>
              </span>
              <span>
                <i style={{ background: COLORS.line }} />
                {t('homePage.missionsTab.todo', { defaultValue: 'To do' })}{' '}
                <b className="dmt-mono">{taskStats.todo}</b>
              </span>
            </div>
            <div className="dmt-mini-list">
              {taskStats.mini.map((r) => (
                <div className="dmt-mini-row" key={r.k}>
                  <span className="dmt-mini-k">{r.k}</span>
                  <span className="dmt-bar-track">
                    <span style={{ width: `${r.w}%`, background: r.c }} />
                  </span>
                  <span className="dmt-mini-v">{r.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
